import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Edit, Trash2, Upload, Download, FileSpreadsheet, Search, Package, Settings, FlaskConical, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { getUser } from "../utils/authService";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BOQManagement = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Recipe dialog state
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [selectedBoqForRecipe, setSelectedBoqForRecipe] = useState(null);
  const [recipeItems, setRecipeItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  
  const [formData, setFormData] = useState({
    item_code: "",
    description: "",
    unit: "Cum",
    planned_quantity: "",
    rate: ""
  });

  const [searchTerm, setSearchTerm] = useState("");

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.role === "Master Admin";

  useEffect(() => {
    fetchProject();
    fetchBOQItems();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error("Failed to fetch project");
    }
  };

  const fetchBOQItems = async () => {
    try {
      const response = await apiClient.get(`/boq/${projectId}`);
      setBoqItems(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || "You don't have permission to access BOQ. Contact admin.");
      } else {
        toast.error("Failed to fetch BOQ items");
      }
    }
  };

  // Fetch materials for recipe management
  const fetchMaterials = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/${projectId}`);
      setMaterials(response.data);
    } catch (error) {
      console.error("Failed to fetch materials");
    }
  }, [projectId]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // Open recipe dialog for a BOQ item
  const openRecipeDialog = async (boqItem) => {
    setSelectedBoqForRecipe(boqItem);
    setLoadingRecipe(true);
    setRecipeDialogOpen(true);
    
    try {
      const response = await apiClient.get(`/boq/${boqItem.id}/recipe`);
      const existingRecipe = response.data.material_recipe || [];
      setRecipeItems(existingRecipe.map((item, idx) => ({
        ...item,
        _key: Date.now() + idx
      })));
    } catch (error) {
      console.error("Failed to fetch recipe:", error);
      setRecipeItems([]);
    } finally {
      setLoadingRecipe(false);
    }
  };

  // Add a new material to the recipe
  const addRecipeItem = () => {
    setRecipeItems([
      ...recipeItems,
      { 
        material_id: "", 
        material_name: "", 
        quantity_per_unit: "", 
        unit: "",
        _key: Date.now()
      }
    ]);
  };

  // Update a recipe item
  const updateRecipeItem = (index, field, value) => {
    const updated = [...recipeItems];
    updated[index][field] = value;
    
    // If material_id changes, update material_name and unit
    if (field === "material_id") {
      const material = materials.find(m => m.id === value);
      if (material) {
        updated[index].material_name = material.name;
        updated[index].unit = material.unit;
      }
    }
    
    setRecipeItems(updated);
  };

  // Remove a recipe item
  const removeRecipeItem = (index) => {
    setRecipeItems(recipeItems.filter((_, i) => i !== index));
  };

  // Save the recipe
  const saveRecipe = async () => {
    if (!selectedBoqForRecipe) return;
    
    // Validate recipe items
    const validItems = recipeItems.filter(item => item.material_id && parseFloat(item.quantity_per_unit) > 0);
    if (recipeItems.length > 0 && validItems.length === 0) {
      toast.error("Please add at least one valid material with quantity");
      return;
    }
    
    setSavingRecipe(true);
    try {
      const recipeData = validItems.map(item => ({
        material_id: item.material_id,
        material_name: item.material_name,
        quantity_per_unit: parseFloat(item.quantity_per_unit),
        unit: item.unit
      }));
      
      await apiClient.put(`/boq/${selectedBoqForRecipe.id}/recipe`, recipeData);
      toast.success(`Recipe saved for ${selectedBoqForRecipe.item_code}`);
      setRecipeDialogOpen(false);
      setSelectedBoqForRecipe(null);
      setRecipeItems([]);
      fetchBOQItems(); // Refresh to show recipe indicator
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save recipe");
    } finally {
      setSavingRecipe(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        // For update, only send fields that BOQItemUpdate expects
        const updateData = {
          item_code: formData.item_code,
          description: formData.description,
          unit: formData.unit,
          planned_quantity: parseFloat(formData.planned_quantity),
          rate: parseFloat(formData.rate)
        };
        await apiClient.put(`/boq/${editingItem.id}`, updateData);
        toast.success("BOQ Item Updated");
      } else {
        // For create, include project_id
        const createData = {
          ...formData,
          project_id: projectId,
          planned_quantity: parseFloat(formData.planned_quantity),
          rate: parseFloat(formData.rate)
        };
        await apiClient.post('/boq', createData);
        toast.success("BOQ Item Added");
      }
      
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({
        item_code: "",
        description: "",
        unit: "Cum",
        planned_quantity: "",
        rate: ""
      });
      fetchBOQItems();
    } catch (error) {
      toast.error(`Failed to ${editingItem ? 'update' : 'add'} BOQ item`);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_code: item.item_code,
      description: item.description,
      unit: item.unit,
      planned_quantity: item.planned_quantity.toString(),
      rate: item.rate.toString()
    });
    setDialogOpen(true);
  };

  const handleDelete = async (itemId, itemCode) => {
    if (!window.confirm(`Are you sure you want to delete BOQ item "${itemCode}"?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/boq/${itemId}`);
      toast.success("BOQ item deleted successfully");
      fetchBOQItems();
    } catch (error) {
      toast.error("Failed to delete BOQ item");
    }
  };

  const openNewItemDialog = () => {
    setEditingItem(null);
    setFormData({
      item_code: "",
      description: "",
      unit: "Cum",
      planned_quantity: "",
      rate: ""
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post(`/boq/upload/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      toast.success(`Imported ${response.data.imported_count} BOQ items`);
      fetchBOQItems();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to upload file";
      toast.error(detail);
      setUploadResult({ error: detail });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiClient.get(`/boq/template/${projectId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BOQ_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const totalPlannedValue = boqItems.reduce((sum, item) => sum + item.amount, 0);
  const totalCompletedValue = boqItems.reduce((sum, item) => sum + (item.completed_quantity * item.rate), 0);
  const overallProgress = totalPlannedValue > 0 ? (totalCompletedValue / totalPlannedValue * 100) : 0;

  if (!project) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <BackNavigation title="BOQ Management" projectName={project?.name} />
      
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">BILL OF QUANTITIES</h1>
          <p className="text-slate-600 mt-2 font-body">{project.name}</p>
        </div>
          <div className="flex space-x-2">
          {/* Excel Upload Button */}
          <button
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-boq-button"
            className="btn-outline flex items-center space-x-2 border-success text-success hover:bg-success/10"
          >
            <Upload className="w-4 h-4" />
            <span>Upload Excel</span>
          </button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button onClick={openNewItemDialog} data-testid="add-boq-item-button" className="btn-primary flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Item</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editingItem ? 'EDIT BOQ ITEM' : 'ADD BOQ ITEM'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Item Code</label>
                  <input
                    type="text"
                    data-testid="boq-item-code-input"
                    required
                    value={formData.item_code}
                    onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="EW-001"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description</label>
                  <input
                    type="text"
                    data-testid="boq-description-input"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Earthwork in excavation for foundation"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unit</label>
                    <select
                      data-testid="boq-unit-select"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="Cum">Cum</option>
                      <option value="Sqm">Sqm</option>
                      <option value="Rm">Rm</option>
                      <option value="MT">MT</option>
                      <option value="Nos">Nos</option>
                      <option value="Km">Km</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="boq-quantity-input"
                      required
                      value={formData.planned_quantity}
                      onChange={(e) => setFormData({ ...formData, planned_quantity: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      placeholder="1000.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Rate (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="boq-rate-input"
                      required
                      value={formData.rate}
                      onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      placeholder="450.00"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => {
                    setDialogOpen(false);
                    setEditingItem(null);
                  }} className="btn-outline">
                    Cancel
                  </button>
                  <button type="submit" data-testid="submit-boq-item-button" className="btn-primary">
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total BOQ Value</p>
          <p className="font-heading font-black text-3xl text-primary font-mono" data-testid="total-boq-value">₹{(totalPlannedValue / 10000000).toFixed(2)} Cr</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Completed Value</p>
          <p className="font-heading font-black text-3xl text-success font-mono" data-testid="completed-value">₹{(totalCompletedValue / 10000000).toFixed(2)} Cr</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Overall Progress</p>
          <p className="font-heading font-black text-3xl text-primary font-mono" data-testid="overall-progress">{overallProgress.toFixed(1)}%</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by item code or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            data-testid="boq-search-input"
          />
        </div>
      </div>

      <div className="stat-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Item Code</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Description</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Unit</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Planned Qty</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Completed Qty</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Rate (₹)</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Amount (₹)</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Progress %</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Recipe</th>
              {isMasterAdmin && (
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {boqItems
              .filter(item => !searchTerm || 
                item.item_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.description.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((item) => {
              const progress = (item.completed_quantity / item.planned_quantity * 100).toFixed(1);
              return (
                <tr key={item.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`boq-item-row-${item.id}`}>
                  <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{item.item_code}</td>
                  <td className="py-3 px-4 text-sm">{item.description}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.unit}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.planned_quantity.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-success">{item.completed_quantity.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.rate.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-semibold">{item.amount.toLocaleString('en-IN')}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={`inline-block px-2 py-1 rounded-sm text-xs font-mono font-bold ${
                      parseFloat(progress) >= 100 ? 'bg-success/20 text-success' :
                      parseFloat(progress) >= 50 ? 'bg-warning/20 text-warning' :
                      'bg-slate-200 text-slate-700'
                    }`}>
                      {progress}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => openRecipeDialog(item)}
                      data-testid={`recipe-boq-${item.id}`}
                      className={`p-1.5 rounded transition-colors ${
                        item.material_recipe && item.material_recipe.length > 0 
                          ? 'bg-success/20 hover:bg-success/30 text-success' 
                          : 'hover:bg-primary/10 text-slate-400 hover:text-primary'
                      }`}
                      title={item.material_recipe && item.material_recipe.length > 0 
                        ? `Recipe: ${item.material_recipe.length} materials` 
                        : "Set Material Recipe"}
                    >
                      <FlaskConical className="w-4 h-4" />
                      {item.material_recipe && item.material_recipe.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-success text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                          {item.material_recipe.length}
                        </span>
                      )}
                    </button>
                  </td>
                  {isMasterAdmin && (
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-boq-${item.id}`}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-primary" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.item_code)}
                          data-testid={`delete-boq-${item.id}`}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-error" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {boqItems.filter(item => !searchTerm || 
          item.item_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase())
        ).length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            {searchTerm ? `No BOQ items found matching "${searchTerm}"` : 'No BOQ items added yet. Click "Add Item" or "Upload Excel" to get started.'}
          </div>
        )}
      </div>

      {/* Excel Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center space-x-2">
              <FileSpreadsheet className="w-6 h-6 text-success" />
              <span>UPLOAD BOQ FROM EXCEL</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-sm">
              <h4 className="font-medium text-sm mb-2">Required Columns:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Item_Code</strong> - Unique code for each item</li>
                <li>• <strong>Description</strong> - Item description</li>
                <li>• <strong>Unit</strong> - Unit of measurement (Cum, Sqm, MT, etc.)</li>
                <li>• <strong>Quantity</strong> - Planned quantity</li>
                <li>• <strong>Rate</strong> - Unit rate in ₹</li>
              </ul>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={downloadTemplate}
                className="btn-outline flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download Template</span>
              </button>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center">
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                id="boq-file-upload"
                data-testid="boq-file-input"
              />
              <label
                htmlFor="boq-file-upload"
                className="cursor-pointer"
              >
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  {uploading ? "Uploading..." : "Click to upload Excel/CSV file"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Supports .xlsx, .xls, .csv
                </p>
              </label>
            </div>

            {uploadResult && (
              <div className={`p-4 rounded-sm ${uploadResult.error ? 'bg-error/10' : 'bg-success/10'}`}>
                {uploadResult.error ? (
                  <p className="text-error text-sm">{uploadResult.error}</p>
                ) : (
                  <>
                    <p className="text-success font-medium">
                      ✓ Imported {uploadResult.imported_count} items
                    </p>
                    {uploadResult.total_errors > 0 && (
                      <div className="mt-2">
                        <p className="text-warning text-sm">
                          {uploadResult.total_errors} rows had errors:
                        </p>
                        <ul className="text-xs text-slate-600 mt-1">
                          {uploadResult.errors.map((err, idx) => (
                            <li key={idx}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadResult(null);
                }}
                className="btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipe Management Dialog */}
      <Dialog open={recipeDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setRecipeDialogOpen(false);
          setSelectedBoqForRecipe(null);
          setRecipeItems([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center space-x-2">
              <FlaskConical className="w-6 h-6 text-primary" />
              <span>MATERIAL RECIPE</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedBoqForRecipe && (
            <div className="space-y-4 mt-4">
              {/* BOQ Item Info */}
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Item Code</p>
                    <p className="font-mono font-bold text-primary">{selectedBoqForRecipe.item_code}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Unit</p>
                    <p className="font-mono">{selectedBoqForRecipe.unit}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Description</p>
                  <p className="text-sm">{selectedBoqForRecipe.description}</p>
                </div>
              </div>

              {/* Explanation */}
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-sm text-amber-800">
                  <strong>Material Recipe:</strong> Define which raw materials are needed to execute 1 {selectedBoqForRecipe.unit} of this BOQ item. 
                  When you create BOQ-linked material outward, the system will automatically calculate required quantities based on executed work.
                </p>
              </div>

              {/* Recipe Items */}
              {loadingRecipe ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                  <p className="text-slate-500 mt-2">Loading recipe...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-sm uppercase tracking-widest text-slate-700">
                      Materials per 1 {selectedBoqForRecipe.unit}
                    </h4>
                    <button
                      onClick={addRecipeItem}
                      className="btn-outline text-sm px-3 py-1 flex items-center space-x-1"
                      type="button"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add Material</span>
                    </button>
                  </div>

                  {recipeItems.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-sm">
                      <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500 mb-3">No materials in recipe</p>
                      <button
                        onClick={addRecipeItem}
                        className="btn-primary text-sm"
                        type="button"
                      >
                        <Plus className="w-4 h-4 inline mr-1" />
                        Add First Material
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Header */}
                      <div className="grid grid-cols-12 gap-2 px-2 py-1 bg-slate-100 rounded-sm text-xs uppercase tracking-widest text-slate-600">
                        <div className="col-span-5">Material</div>
                        <div className="col-span-3 text-right">Qty per {selectedBoqForRecipe.unit}</div>
                        <div className="col-span-2">Unit</div>
                        <div className="col-span-2 text-center">Action</div>
                      </div>
                      
                      {recipeItems.map((item, index) => (
                        <div key={item._key} className="grid grid-cols-12 gap-2 items-center p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
                          <div className="col-span-5">
                            <select
                              value={item.material_id}
                              onChange={(e) => updateRecipeItem(index, "material_id", e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              data-testid={`recipe-material-${index}`}
                            >
                              <option value="">Select Material</option>
                              {materials.map((mat) => (
                                <option key={mat.id} value={mat.id}>
                                  {mat.name} ({mat.unit})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-3">
                            <input
                              type="number"
                              step="0.001"
                              min="0.001"
                              value={item.quantity_per_unit}
                              onChange={(e) => updateRecipeItem(index, "quantity_per_unit", e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm text-right"
                              placeholder="0.00"
                              data-testid={`recipe-qty-${index}`}
                            />
                          </div>
                          <div className="col-span-2">
                            <span className="text-sm text-slate-600 font-mono">
                              {item.unit || "-"}
                            </span>
                          </div>
                          <div className="col-span-2 text-center">
                            <button
                              onClick={() => removeRecipeItem(index)}
                              className="p-1.5 hover:bg-red-100 rounded text-error"
                              title="Remove"
                              type="button"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Example Calculation */}
                  {recipeItems.length > 0 && recipeItems.some(item => item.material_id && parseFloat(item.quantity_per_unit) > 0) && (
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-4">
                      <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Example: For 10 {selectedBoqForRecipe.unit} of work</p>
                      <ul className="text-sm space-y-1">
                        {recipeItems
                          .filter(item => item.material_id && parseFloat(item.quantity_per_unit) > 0)
                          .map((item, idx) => (
                            <li key={idx} className="flex justify-between">
                              <span>{item.material_name || "Material"}</span>
                              <span className="font-mono font-medium">
                                {(parseFloat(item.quantity_per_unit) * 10).toFixed(3)} {item.unit}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setRecipeDialogOpen(false);
                    setSelectedBoqForRecipe(null);
                    setRecipeItems([]);
                  }}
                  className="btn-outline"
                  type="button"
                  disabled={savingRecipe}
                >
                  Cancel
                </button>
                <button
                  onClick={saveRecipe}
                  className="btn-primary flex items-center space-x-2"
                  disabled={savingRecipe}
                  data-testid="save-recipe-btn"
                >
                  {savingRecipe ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Recipe</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BOQManagement;